import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Camera, Trash2, Download, X } from "lucide-react";
import { toast } from "sonner";

// Image src for a saved snapshot (auth via ?auth= query, same pattern as photos).
const snapshotSrc = (id) => {
  const tok = localStorage.getItem("fp_token") || "";
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/snapshots/${id}/file?auth=${encodeURIComponent(tok)}`;
};

export default function SnapshotTimeline() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/snapshots");
      setItems(r.data || []);
    } catch {
      // 401/404 etc. — leave empty
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    try {
      await api.delete(`/snapshots/${id}`);
      setItems((s) => s.filter((x) => x.id !== id));
      if (viewing?.id === id) setViewing(null);
      toast.success("Snapshot deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const onDownload = (id) => {
    const a = document.createElement("a");
    a.href = snapshotSrc(id);
    a.download = `ripple-effect-${id}.png`;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="obs-card" data-testid="snapshot-timeline">
      <div className="flex items-center gap-3 mb-4">
        <Camera className="h-5 w-5 text-blaze" />
        <div>
          <div className="overline">Past snapshots</div>
          <h3 className="font-heading text-2xl uppercase">Your shared moments</h3>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <p className="text-zinc-500 text-sm">
          Snapshots you generate from the Dashboard or Coach Console will appear here.
          Tap <span className="text-zinc-200">Share my 30 days</span> to create your first one.
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="snapshot-grid">
          {items.map((s) => (
            <div
              key={s.id}
              className="group relative border border-obsidian-600 rounded-sm overflow-hidden bg-obsidian-900/40"
              data-testid={`snapshot-card-${s.id}`}
            >
              <button
                type="button"
                onClick={() => setViewing(s)}
                className="block w-full aspect-square"
              >
                <img
                  src={snapshotSrc(s.id)}
                  alt="snapshot"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </button>
              <div className="absolute inset-x-0 bottom-0 bg-obsidian-900/85 px-2 py-1 flex items-center justify-between text-[10px] uppercase tracking-widest">
                <span className="text-zinc-300">
                  {new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </span>
                <span className="text-blaze">{s.kind}</span>
              </div>
              <div className="absolute top-1 right-1 hidden group-hover:flex gap-1">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onDownload(s.id); }}
                  className="bg-obsidian-900/90 border border-obsidian-600 rounded-sm p-1 text-zinc-300 hover:text-blaze"
                  data-testid={`snapshot-dl-${s.id}`}
                  aria-label="Download"
                >
                  <Download className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
                  className="bg-obsidian-900/90 border border-obsidian-600 rounded-sm p-1 text-zinc-300 hover:text-blaze"
                  data-testid={`snapshot-del-${s.id}`}
                  aria-label="Delete"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {viewing && (
        <div
          className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-4"
          onClick={() => setViewing(null)}
          data-testid="snapshot-lightbox"
        >
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setViewing(null)}
              className="absolute -top-10 right-0 text-zinc-400 hover:text-white inline-flex items-center gap-1 text-xs uppercase tracking-widest"
              data-testid="snapshot-lightbox-close"
            >
              <X className="h-3 w-3" /> Close
            </button>
            <img
              src={snapshotSrc(viewing.id)}
              alt="snapshot"
              className="w-full rounded-sm border border-obsidian-600"
            />
            <div className="mt-3 flex items-center justify-between text-xs text-zinc-400 uppercase tracking-widest">
              <span>{new Date(viewing.created_at).toLocaleString()}</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onDownload(viewing.id)}
                  className="ghost-btn px-3 py-1.5 text-[10px] inline-flex items-center gap-2"
                >
                  <Download className="h-3 w-3" /> Download
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(viewing.id)}
                  className="ghost-btn px-3 py-1.5 text-[10px] inline-flex items-center gap-2"
                >
                  <Trash2 className="h-3 w-3" /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
